/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { cached } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import UiBookingSummaryV1Component, {
  type IBookingAction,
  timeOptionToTime,
} from 'uplisting-frontend/components/ui/booking/summary/v1';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { BookingSource } from 'uplisting-frontend/models/booking';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export default class UiBookingSummaryV2Component extends UiBookingSummaryV1Component {
  @service('booking-drawer')
  bookingDrawer!: Services['booking-drawer'];

  sources = Object.values(BookingSource);

  @cached
  get modifyRoute(): string {
    const currentRouteName = this.router.currentRouteName ?? '';

    if (currentRouteName.includes('monthly')) {
      return 'monthly.bookings.summary.modify';
    } else if (currentRouteName.includes('action')) {
      return 'action.bookings.summary.modify';
    } else if (currentRouteName.startsWith('xcalendar')) {
      return 'xcalendar.bookings.summary.modify';
    } else if (currentRouteName.startsWith('protection')) {
      return 'protection.overview.bookings.summary.modify';
    }

    return 'calendar.bookings.summary.modify';
  }

  @cached
  get bookingActions(): IBookingAction[] {
    return super.bookingActions.map((action) => {
      if (
        action.translationKey !==
        'action_bookings_summary.actions.alteration_request'
      ) {
        return action;
      }
      return {
        ...action,
        onApply: () => {
          if (this.booking.pendingAlterationRequest) return;
          this.router.transitionTo(this.modifyRoute, this.booking.id);
        },
      };
    });
  }

  @cached
  get acceptRoute(): string {
    const currentRouteName = this.router.currentRouteName ?? '';

    if (currentRouteName.includes('monthly')) {
      return 'monthly.bookings.summary.accept';
    } else if (currentRouteName.includes('action')) {
      return 'action.bookings.summary.accept';
    } else if (currentRouteName.startsWith('xcalendar')) {
      return 'xcalendar.bookings.summary.accept';
    } else if (currentRouteName.startsWith('protection')) {
      return 'protection.overview.bookings.summary.accept';
    }

    return 'calendar.bookings.summary.accept';
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking']> {
    return this.bookingDrawer.changesetFor(this.booking);
  }

  @action
  handleSelectSource(source: BookingSource): void {
    this.booking.source = source;
  }

  @action
  handleSaveExpiresIn(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    if (isEmpty(value)) {
      return;
    }

    this.changeset.expiresIn = Number(value);
  }

  @action
  @waitFor
  async handleSelectTime(
    field: 'arrivalTime' | 'departureTime',
    option: string,
  ): Promise<void> {
    this.booking[field] = timeOptionToTime(option);
  }

  protected async saveBookingNow(
    attributes: Partial<ModelRegistry['booking']>,
  ): Promise<void> {
    await this.bookingDrawer.saveNow(this.booking, attributes);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Summary::V2': typeof UiBookingSummaryV2Component;
  }
}
